import { useInput } from "@redotech/react-util/form";
import { usePickupModals } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-pickup-modals";
import { PickupLocation } from "@redotech/redo-model/return";
import { pickupLocationOptions } from "@redotech/redo-model/return-flow";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input } from "@redotech/ui/form";
import { useContext } from "react";

export const PackagePickupModals = ({
  pickupModals,
}: {
  pickupModals: ReturnType<typeof usePickupModals>;
}) => {
  const {
    rescheduleModalOpen,
    setRescheduleModalOpen,
    cancelModalOpen,
    setCancelModalOpen,
    pickupFee,
    pickupInfo,
    minDate,
    maxDate,
    reschedulePending,
    cancelPending,
    handleReschedulePickup,
    handleCancelPickup,
    hasRescheduleError,
    hasCancelError,
  } = pickupModals;

  const handleRescheduleModalClose = () => {
    setRescheduleModalOpen(false);
  };

  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
  };

  return (
    <>
      <ReschedulePickupModal
        error={hasRescheduleError}
        isOpen={rescheduleModalOpen}
        maxDate={maxDate}
        minDate={minDate}
        onClose={handleRescheduleModalClose}
        onPrimaryButtonClick={handleReschedulePickup}
        pending={reschedulePending}
        pickupInfo={pickupInfo}
      />
      <CancelPickupModal
        error={hasCancelError}
        isOpen={cancelModalOpen}
        onClose={handleCancelModalClose}
        onPrimaryButtonClick={handleCancelPickup}
        pending={cancelPending}
        pickupFee={pickupFee}
      />
    </>
  );
};

const reschedulePickupForm = groupInput({
  pickupDate: input<Temporal.PlainDate | undefined>({
    validator: (date) => (date !== undefined ? [] : ["Required"]),
  }),
  location: input<PickupLocation | undefined>({
    validator: (location) => (location !== undefined ? [] : ["Required"]),
  }),
});

const locationOptions: RedoListItem<PickupLocation>[] = Array.from(
  pickupLocationOptions.values(),
).map((option) => ({
  id: option.value,
  value: option.value,
  type: "text",
  text: option.label,
}));

const ReschedulePickupModal = ({
  isOpen,
  onClose,
  pickupInfo,
  minDate,
  maxDate,
  pending,
  onPrimaryButtonClick,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  minDate: Temporal.PlainDate;
  maxDate: Temporal.PlainDate;
  pickupInfo: {
    pickupDate: Temporal.PlainDate | undefined;
    location: PickupLocation | undefined;
  };
  pending: boolean;
  onPrimaryButtonClick: ReturnType<
    typeof usePickupModals
  >["handleReschedulePickup"];
  error: boolean;
}) => {
  const input = useInput(reschedulePickupForm, pickupInfo);
  const { pickupDate, location } = input.inputs;
  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        onClick: () => onPrimaryButtonClick(input.value),
        text: "Reschedule pickup",
        size: "sm",
        pending,
      }}
      subtitle="Try rescheduling the pickup. If you’re still having trouble, you can cancel and refund the pickup."
      title="Reschedule pickup"
    >
      <Flex dir="column" gap="xl">
        <Flex dir="column" gap="md">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Pickup date
          </Text>
          <Flex>
            <RedoSingleDatePicker
              date={pickupDate.value}
              maxDate={maxDate}
              minDate={minDate}
              setDate={(newDate) => pickupDate.setValue(newDate)}
            />
          </Flex>
        </Flex>
        <Flex dir="column" gap="sm">
          <RedoSingleSelectDropdownInput
            dataTestId="pickup-location-dropdown"
            label="Pickup location"
            options={locationOptions}
            optionSelected={({ item }) => location.setValue(item.value)}
            selectedItem={locationOptions.find(
              (option) => option.value === location.value,
            )}
            size="xs"
          />
          <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
            If you live in a multi-story building, select an option which is
            ground level and secure.
          </Text>
        </Flex>

        {error && (
          <Text fontSize="sm" textColor="error">
            There was an error rescheduling the pickup. Please try again with a
            different date, or you may cancel and refund the pickup.
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
};

const CancelPickupModal = ({
  isOpen,
  onClose,
  pickupFee,
  pending,
  onPrimaryButtonClick,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  pickupFee: number;
  pending: boolean;
  onPrimaryButtonClick: () => void;
  error: boolean;
}) => {
  const { formatCurrency } = useContext(CurrencyContext);

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        onClick: onPrimaryButtonClick,
        text: "Cancel and refund pickup",
        size: "sm",
        pending,
      }}
      title="Cancel and refund pickup"
    >
      <Flex dir="column" gap="xl">
        <Text>
          This will cancel the pickup request and refund the amount paid for the
          pickup. You will not be able to reschedule the pickup. The refund
          totaling <strong>{formatCurrency(pickupFee)}</strong> will be refunded
          to the original payment method once the package is dropped off.
        </Text>
        {error && (
          <Text fontSize="sm" textColor="error">
            There was an error cancelling the pickup. Please reach out to
            support.
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
};
